/**
 * #580 live updates, unit 11 part 2 — the child's approval echo: the ORIGINAL lantern beside one sentence, and
 * for an approval one restrained amber light sweep.
 *
 * Non-modal and read-only. It writes no money, points, completion or approval, and it is not the completion
 * celebration: it does not open `CelebrationOverlay`, call its commit/undo callbacks or throw its coins. It
 * shares that ritual's art (`LanternArt`, `LANTERN_MARK`) and its stylesheet (`lantern-ritual.css`), where the
 * one reduced-motion rule stills both.
 *
 * What each card may say is fixed here (design §4's table, private child view):
 *   approved  "High-five! Your quest “X” was approved." plus "Approval isn’t a payment. Your wallet shows money
 *             already paid." No amount, no payday promise, no deposit animation.
 *   held      "Your quest “X” needs another look." plus the parent's own words, then "Ask your parent about the
 *             next step." Calm: no sweep.
 * Never a payday hold, a wait or a talk — those are not approval decisions, and this card never sees them.
 *
 * One polite, atomic status region carries the whole sentence; it exists empty from mount, so an old row is
 * never announced at mount and a new one is. The decorative lantern is `alt=""`, and Dismiss sits OUTSIDE the
 * region so it is not read as part of the message. Any deliberate input skips the sweep without dismissing.
 */
import React, { useEffect, useState } from 'react';

import LanternArt from './LanternArt.jsx';
import { LANTERN_MARK } from '../assets.js';
import '../styles/lantern-ritual.css';

export const SWEEP_MS = 800;
export const NOT_A_PAYMENT = 'Approval isn’t a payment. Your wallet shows money already paid.';
export const NEXT_STEP = 'Ask your parent about the next step.';

export function approvedHeadline(items) {
  return items.length === 1
    ? `High-five! Your quest “${items[0].questTitle}” was approved.`
    : `High-five! ${items.length} of your quests were approved.`;
}

export function heldHeadline(items) {
  return items.length === 1
    ? `Your quest “${items[0].questTitle}” needs another look.`
    : `${items.length} of your quests need another look.`;
}

const parentWrote = (reason) => `A parent wrote: “${reason}”`;

function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => {
    try { return window.matchMedia(query).matches; } catch { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(query); } catch { return undefined; }
    const sync = () => setReduced(mq.matches);
    sync();
    if (mq.addEventListener) {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener?.(sync);
    return () => mq.removeListener?.(sync);
  }, []);
  return reduced;
}

/**
 * @param {object} props
 * @param {{approved: object[], held: object[]}|null} props.echo  what the review read newly decided
 * @param {() => void} props.onDismiss
 * @param {string} [props.lanternImage]
 */
export default function ApprovalEcho({ echo, onDismiss, lanternImage = LANTERN_MARK }) {
  const reducedMotion = usePrefersReducedMotion();
  const approved = echo?.approved ?? [];
  const held = echo?.held ?? [];
  const shown = approved.length + held.length > 0;
  const kind = approved.length > 0 ? 'approved' : held.length > 0 ? 'held' : null;

  const [sweeping, setSweeping] = useState(false);
  useEffect(() => {
    // A card that also lists held work stays calm (design §4): the sweep is for an approval alone.
    if (kind !== 'approved' || held.length > 0 || reducedMotion) { setSweeping(false); return undefined; }
    setSweeping(true);
    const stop = () => setSweeping(false);
    const timer = setTimeout(stop, SWEEP_MS);
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    events.forEach((name) => document.addEventListener(name, stop, { capture: true, passive: true }));
    return () => {
      clearTimeout(timer);
      events.forEach((name) => document.removeEventListener(name, stop, true));
    };
  }, [echo, kind, held.length, reducedMotion]);

  return (
    <section
      className={shown ? `approval-echo approval-echo--${kind}` : 'approval-echo approval-echo--empty'}
      data-component="ApprovalEcho"
      data-kind={kind ?? undefined}
      aria-label={shown ? 'A parent reviewed your quests' : undefined}
    >
      {shown ? (
        <LanternArt
          src={lanternImage}
          alt=""
          className="approval-echo__art"
          fallback={<span className="approval-echo__art approval-echo__art--fallback" aria-hidden="true" />}
        />
      ) : null}
      {shown && sweeping ? <span className="approval-echo__sweep" data-part="sweep" aria-hidden="true" /> : null}
      <div className="approval-echo__text" role="status" aria-live="polite" aria-atomic="true">
        {approved.length > 0 ? (
          <div data-part="approved">
            <p className="approval-echo__headline">{approvedHeadline(approved)}</p>
            {approved.length > 1 ? <ul>{approved.map((r) => <li key={r.completionId}>{r.questTitle}</li>)}</ul> : null}
            <p>{NOT_A_PAYMENT}</p>
          </div>
        ) : null}
        {held.length > 0 ? (
          <div data-part="held">
            <p className="approval-echo__headline">{heldHeadline(held)}</p>
            {held.length > 1 ? (
              <ul>
                {held.map((r) => (
                  <li key={r.completionId}>{r.questTitle}{r.reason ? ` — ${parentWrote(r.reason)}` : ''}</li>
                ))}
              </ul>
            ) : held[0].reason ? <p>{parentWrote(held[0].reason)}</p> : null}
            <p>{NEXT_STEP}</p>
          </div>
        ) : null}
      </div>
      {shown ? <button type="button" className="approval-echo__dismiss" onClick={onDismiss}>Dismiss</button> : null}
    </section>
  );
}
